/**
 * Module dependencies.
 */

import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { AxiosInstance } from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * `MovementsStats` type.
 */

export type MovementsStats = {
  total: number;
  uncategorized: number;
  lastDate: string | null;
};

/**
 * Fetch a single-row page of `/transactions` and return { total, results }.
 *
 * Params mirror the CRUD list contract: `filters` is nested (serialised as
 * `filters[field]=value`) and `order` is `[{ key, direction }]`.
 */

function fetchPage(client: AxiosInstance, params: Record<string, any>): Promise<{ total: number; results: any[] }> {
  return client
    .get(resolveAppUrl('transactions', { params: { page: 1, pageSize: 1, execute: true, ...params } }))
    .then(({ data }) => {
      const payload = data?.data ?? {};

      return { total: payload.total ?? 0, results: payload.results ?? [] };
    });
}

/**
 * Export `useMovementsStatsInvalidator`.
 */

export function useMovementsStatsInvalidator() {
  const queryClient = useQueryClient();

  return useCallback(() => queryClient.invalidateQueries(['movementsStats']), [queryClient]);
}

/**
 * Export `useMovementsStats`.
 *
 * Derives lightweight dashboard stats from the transactions list: total count,
 * uncategorized count (category `-1` is the app's "empty" sentinel) and the most
 * recent movement date. Two 1-row queries — no dedicated endpoint required.
 */

export function useMovementsStats() {
  const client = useRequestProvider();

  return useQuery<MovementsStats>(['movementsStats'], {
    queryFn: async () => {
      const [all, uncategorized] = await Promise.all([
        // Latest movement first, for total + last-update date.
        fetchPage(client, { order: [{ key: 'bank_date', direction: 'desc' }] }),
        // Empty category value -> backend `whereNull(subCategoryId)` (the app's
        // "Missing Category" rows).
        fetchPage(client, { filters: { category: '' } })
      ]);

      return {
        total: all.total,
        uncategorized: uncategorized.total,
        lastDate: all.results?.[0]?.bank_date ?? all.results?.[0]?.date ?? null
      };
    }
  });
}
